import type { AnyManagedComponent, ModalInstance } from '../model/types'

type Listener = () => void

/**
 * Low-level store for the modal system.
 *
 * Maintains a list/map of modal instances and exposes immutable operations
 * to update visibility and props. Rendering is handled by the UI layer.
 *
 * @remarks This is an implementation detail of the modal system.
 * Consumers should use `ModalProvider`, `useModal`, and `useModalState` instead.
 *
 * @private
 */
export class ModalManager {
  private instances = new Map<string, ModalInstance>()
  private order: string[] = []
  private listeners = new Set<Listener>()
  private _snapshot: ModalInstance[] = []

  /**
   * Subscribe to store changes
   * @private
   */
  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /**
   * Get current snapshot for rendering
   * @private
   */
  getSnapshot = (): ModalInstance[] => this._snapshot

  /**
   * Get instance by key
   * @private
   */
  getInstance = (key: string): ModalInstance | undefined => {
    return this.instances.get(key)
  }

  /**
   * Notify all subscribers of state change
   * @private
   */
  private notify = (): void => {
    this.listeners.forEach((listener) => listener())
  }

  /**
   * Recompute snapshot array
   * @private
   */
  private recomputeSnapshot = (): void => {
    this._snapshot = this.order
      .map((k) => this.instances.get(k)!)
      .filter(Boolean)
  }

  /**
   * Create or get existing instance
   * @private
   */
  ensure = (
    key: string,
    component: AnyManagedComponent,
    options?: { destroyOnClose?: boolean },
  ): ModalInstance => {
    let instance = this.instances.get(key)

    if (!instance) {
      instance = {
        id: key, // Stable ID
        key,
        Comp: component,
        props: {},
        open: false,
        destroyOnClose: options?.destroyOnClose ?? false,
      }

      this.instances.set(key, instance)
      this.order.push(key)
      this.recomputeSnapshot()
    }

    return instance
  }

  /**
   * Show a modal (updates visibility)
   * @private
   */
  show = (key: string): void => {
    const instance = this.instances.get(key)
    if (!instance || instance.open) return

    this.instances.set(key, { ...instance, open: true })
    this.recomputeSnapshot()
    this.notify()
  }

  /**
   * Hide a modal
   * @private
   */
  hide = (key: string): void => {
    const instance = this.instances.get(key)
    if (!instance || !instance.open) return

    // IMMUTABLE UPDATE
    this.instances.set(key, { ...instance, open: false })
    this.recomputeSnapshot()
    this.notify()
  }

  /**
   * Destroy a modal
   * @private
   */
  destroy = (key: string): void => {
    if (!this.instances.has(key)) return

    this.instances.delete(key)
    this.order = this.order.filter((k) => k !== key)
    this.recomputeSnapshot()
    this.notify()
  }

  /**
   * Update modal props and notify listeners
   * Used by useLayoutEffect to sync props from parent to store
   * @private
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateProps = (key: string, props: Record<string, any>): void => {
    const instance = this.instances.get(key)
    if (!instance) return

    this.instances.set(key, { ...instance, props })
    this.recomputeSnapshot()
    this.notify()
  }

  /**
   * Merge props into existing modal props
   * @private
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mergeProps = (key: string, props: Record<string, any>): void => {
    const instance = this.instances.get(key)
    if (!instance) return

    this.instances.set(key, {
      ...instance,
      props: { ...instance.props, ...props },
    })
    this.recomputeSnapshot()
    this.notify()
  }

  /**
   * Check if modal is open
   * @private
   */
  isOpen = (key: string): boolean => {
    return this.instances.get(key)?.open ?? false
  }
}
